import React, { useState } from 'react';
import {
  LockClosedIcon,
  LockOpenIcon,
  ArrowsRightLeftIcon,
  ClockIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

interface MealSlotCardProps {
  entry: any;
  mealTypeLabel: string;
  onSwap: () => void;
  onLock: () => void;
}

export const MealSlotCard: React.FC<MealSlotCardProps> = ({
  entry,
  mealTypeLabel,
  onSwap,
  onLock,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Parse custom_meal_data if it's a string
  const mealData = typeof entry.custom_meal_data === 'string'
    ? JSON.parse(entry.custom_meal_data)
    : entry.custom_meal_data;

  const mealName = entry.custom_meal_name || mealData?.name || 'Unnamed Meal';
  const nutrition = entry.nutrition || mealData?.nutrition || {};
  const ingredients = mealData?.ingredients || [];
  const instructions = mealData?.instructions || [];
  const prepTime = entry.prep_time_minutes || mealData?.prepTime || 0;
  const cookTime = entry.cook_time_minutes || mealData?.cookTime || 0;
  const therapeuticNotes = entry.therapeutic_notes || mealData?.therapeuticNotes;

  const getMealTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      breakfast: '🌅',
      morning_snack: '🍎',
      lunch: '☀️',
      afternoon_snack: '🍪',
      dinner: '🌙',
      evening_snack: '🫖',
    };
    return icons[type] || '🍽️';
  };

  return (
    <div className={`px-3 py-2 ${entry.is_locked ? 'bg-amber-50' : ''}`}>
      {/* Meal Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm">{getMealTypeIcon(entry.meal_type)}</span>
            <span className="text-xs font-medium text-gray-500">{mealTypeLabel}</span>
          </div>
          <h4 className="font-medium text-gray-900 text-sm mt-0.5 truncate" title={mealName}>
            {mealName}
          </h4>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={onLock}
            className={`p-1 rounded hover:bg-gray-100 ${entry.is_locked ? 'text-amber-600' : 'text-gray-400'}`}
            title={entry.is_locked ? 'Unlock meal' : 'Lock meal (keep in regenerations)'}
          >
            {entry.is_locked ? (
              <LockClosedIcon className="h-4 w-4" />
            ) : (
              <LockOpenIcon className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={onSwap}
            disabled={entry.is_locked}
            className={`p-1 rounded hover:bg-gray-100 ${entry.is_locked ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500'}`}
            title="Get alternative meal suggestions"
          >
            <ArrowsRightLeftIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 rounded hover:bg-gray-100 text-gray-400"
          >
            {isExpanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Quick Nutrition */}
      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
        <span>{Math.round(nutrition.calories || 0)} cal</span>
        <span className="text-gray-300">|</span>
        <span>{Math.round(nutrition.protein || 0)}g P</span>
        {(prepTime + cookTime) > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-0.5">
              <ClockIcon className="h-3 w-3" />
              {prepTime + cookTime} min
            </span>
          </>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
          {/* Full Nutrition */}
          <div>
            <h5 className="text-xs font-medium text-gray-700 mb-1">Nutrition</h5>
            <div className="grid grid-cols-4 gap-1 text-xs">
              <div className="bg-gray-50 rounded p-1 text-center">
                <div className="font-medium">{Math.round(nutrition.calories || 0)}</div>
                <div className="text-gray-400">cal</div>
              </div>
              <div className="bg-blue-50 rounded p-1 text-center">
                <div className="font-medium text-blue-700">{Math.round(nutrition.protein || 0)}g</div>
                <div className="text-blue-400">protein</div>
              </div>
              <div className="bg-amber-50 rounded p-1 text-center">
                <div className="font-medium text-amber-700">{Math.round(nutrition.carbs || 0)}g</div>
                <div className="text-amber-400">carbs</div>
              </div>
              <div className="bg-rose-50 rounded p-1 text-center">
                <div className="font-medium text-rose-700">{Math.round(nutrition.fat || 0)}g</div>
                <div className="text-rose-400">fat</div>
              </div>
            </div>
          </div>

          {/* Ingredients */}
          {ingredients.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-1">Ingredients</h5>
              <ul className="text-xs text-gray-600 space-y-0.5">
                {ingredients.slice(0, 6).map((ing: any, i: number) => (
                  <li key={i} className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-gray-400 rounded-full" />
                    {ing.quantity} {ing.unit} {ing.name}
                  </li>
                ))}
                {ingredients.length > 6 && (
                  <li className="text-gray-400">+{ingredients.length - 6} more...</li>
                )}
              </ul>
            </div>
          )}

          {/* Instructions Preview */}
          {instructions.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-1">Instructions</h5>
              <ol className="text-xs text-gray-600 space-y-1">
                {instructions.slice(0, 3).map((step: string, i: number) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-gray-400">{i + 1}.</span>
                    <span className="line-clamp-2">{step}</span>
                  </li>
                ))}
                {instructions.length > 3 && (
                  <li className="text-gray-400">+{instructions.length - 3} more steps...</li>
                )}
              </ol>
            </div>
          )}

          {/* Therapeutic Notes */}
          {therapeuticNotes && (
            <div className="bg-teal-50 rounded p-2">
              <div className="flex items-start gap-1.5">
                <InformationCircleIcon className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-teal-700">{therapeuticNotes}</p>
              </div>
            </div>
          )}

          {/* Timing */}
          {(prepTime > 0 || cookTime > 0) && (
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {prepTime > 0 && <span>Prep: {prepTime} min</span>}
              {cookTime > 0 && <span>Cook: {cookTime} min</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
